import { rdEquipamentosService } from '@/services/quotes/rd-equipamentos';
import type { RdProposal } from '@/services/quotes/rd-equipamentos/types';
import { ValidationError } from '@/shared/exceptions/validation.error';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { refetchRdProposalAndQuote } from './refetch-rd-proposal-and-quote.util';

/**
 * Quando o serviço lança uma `ValidationError` (regra de negócio RD, ex.
 * "I.S. acima do limite para esta cobertura."), garante que `.message`
 * reflita o texto real antes de repropagar — mesma prioridade de
 * `use-set-rd-fleet.hook.ts` (`error.detail?.trim() || messages.join('\n') ||
 * error.message`). Os chamadores destas mutações (`step-cotacao.component.tsx`)
 * só leem `error.message` num toast genérico, então sem isso a mensagem de
 * negócio nunca chegaria ao usuário. `fieldErrors`/`detail`/`messages`
 * continuam intactos no objeto relançado.
 */
function rethrowWithBusinessMessage(error: unknown): never {
  if (error instanceof ValidationError) {
    error.message =
      error.detail?.trim() ||
      (error.messages.length ? error.messages.join('\n') : error.message);
  }
  throw error;
}

/**
 * Orquestra as transições de status da proposta RD Equipamentos: no Step 5 —
 * Cotação, calculate/approve/accept ("Gerar proposta")/cancel; no Step 6 —
 * Emissão, transmit (agenda a emissão). A emissão manual em si usa o mesmo
 * botão/modal/endpoint genéricos dos demais produtos — ver
 * `useManualEmissionAction`.
 * Cada ação é um `POST /:proposalId/<action>` sem corpo — o backend só
 * transiciona `status` (e recalcula, no caso de `calculate`); por isso
 * toda ação, no sucesso, refetcha `['rd-proposal', proposalId]` +
 * `['rd-quote', proposalId]` (via `refetchRdProposalAndQuote`) para que o
 * `QuoteBreakdown` e a barra de ações reflitam o novo status/prêmio
 * imediatamente.
 *
 * Erros são apenas propagados (`mutateAsync` rejeita) — o chamador decide
 * como exibi-los (toast), igual a `useRemoveRdEquipment`.
 */
export function useRdLifecycle(proposalId: string | undefined) {
  const queryClient = useQueryClient();

  async function refetchProposalAndQuote(): Promise<void> {
    await refetchRdProposalAndQuote(queryClient, proposalId);
  }

  const calculate = useMutation<RdProposal, Error, void>({
    mutationKey: ['quotes', 'calculate', 'rd-equipamentos'],
    mutationFn: async () => {
      if (!proposalId) throw new Error('ProposalId é obrigatório');
      try {
        const response = await rdEquipamentosService.calculate(proposalId);
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Erro ao calcular cotação');
        }
        return response.data;
      } catch (error) {
        rethrowWithBusinessMessage(error);
      }
    },
    onSuccess: () => refetchProposalAndQuote(),
  });

  const approve = useMutation<RdProposal, Error, void>({
    mutationKey: ['quotes', 'approve', 'rd-equipamentos'],
    mutationFn: async () => {
      if (!proposalId) throw new Error('ProposalId é obrigatório');
      try {
        const response = await rdEquipamentosService.approve(proposalId);
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Erro ao aprovar proposta');
        }
        return response.data;
      } catch (error) {
        rethrowWithBusinessMessage(error);
      }
    },
    onSuccess: () => refetchProposalAndQuote(),
  });

  const accept = useMutation<RdProposal, Error, void>({
    mutationKey: ['quotes', 'accept', 'rd-equipamentos'],
    mutationFn: async () => {
      if (!proposalId) throw new Error('ProposalId é obrigatório');
      try {
        const response = await rdEquipamentosService.accept(proposalId);
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Erro ao gerar proposta');
        }
        return response.data;
      } catch (error) {
        rethrowWithBusinessMessage(error);
      }
    },
    onSuccess: () => refetchProposalAndQuote(),
  });

  const transmit = useMutation<RdProposal, Error, void>({
    mutationKey: ['quotes', 'transmit', 'rd-equipamentos'],
    mutationFn: async () => {
      if (!proposalId) throw new Error('ProposalId é obrigatório');
      try {
        const response = await rdEquipamentosService.transmit(proposalId);
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Erro ao transmitir proposta');
        }
        return response.data;
      } catch (error) {
        rethrowWithBusinessMessage(error);
      }
    },
    // Em modo automático a emissão roda na própria requisição e pode já ter
    // mudado o status; a transmissão sempre muda.
    onSuccess: () =>
      Promise.all([
        refetchProposalAndQuote(),
        queryClient.refetchQueries({
          queryKey: ['rd-transmission', proposalId],
        }),
      ]),
  });

  const cancel = useMutation<RdProposal, Error, void>({
    mutationKey: ['quotes', 'cancel', 'rd-equipamentos'],
    mutationFn: async () => {
      if (!proposalId) throw new Error('ProposalId é obrigatório');
      try {
        const response = await rdEquipamentosService.cancel(proposalId);
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Erro ao cancelar proposta');
        }
        return response.data;
      } catch (error) {
        rethrowWithBusinessMessage(error);
      }
    },
    onSuccess: () => refetchProposalAndQuote(),
  });

  return { calculate, approve, accept, transmit, cancel };
}
